import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from '@/components/base/Modal';
import SegmentedControl from '@/components/base/SegmentedControl';
import KpiCard from '../components/KpiCard';
import { BANNED_CATEGORIES, tierById } from '@/lib/sponsor/catalog';
import { fmtDate, revenueSplit, usd } from '@/lib/sponsor/pricing';
import {
  ORDER_STATUS_LABEL,
  approveOrder,
  confirmTransfer,
  endOrder,
  rejectAccount,
  requestChanges,
  useSponsorStore,
} from '@/lib/sponsor/store';
import type { OrderStatus, SponsorOrder } from '@/lib/sponsor/types';

const FILTERS = ['All', 'To check', 'Waiting payment', 'Live', 'Ended'];
const filterMatch: Record<string, (s: OrderStatus) => boolean> = {
  All: () => true,
  'To check': (s) => s === 'in_review' || s === 'needs_changes',
  'Waiting payment': (s) => s === 'waiting_payment',
  Live: (s) => s === 'live',
  Ended: (s) => s === 'ended',
};

const statusTone: Record<OrderStatus, string> = {
  waiting_payment: 'bg-secondary-100 text-secondary-800',
  in_review: 'bg-primary-100 text-primary-800',
  needs_changes: 'bg-secondary-100 text-secondary-800',
  live: 'bg-accent-100 text-accent-800',
  ended: 'bg-background-200 text-foreground-600',
};

const CHANGE_REASONS = [
  'The banner text is too small to read on a phone.',
  'The licence photo does not match the company name.',
  'The ad is not marked "Sponsored".',
  'The ad breaks the ad rules (alcohol, tobacco, gambling or politics).',
];

export default function SponsorsPage() {
  const store = useSponsorStore();
  const [filter, setFilter] = useState('All');
  const [changeFor, setChangeFor] = useState<SponsorOrder | null>(null);
  const [note, setNote] = useState(CHANGE_REASONS[0]);

  const orders = useMemo(
    () => [...store.orders].filter((o) => filterMatch[filter](o.status)).sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [store.orders, filter],
  );
  const account = (id: string) => store.accounts.find((a) => a.id === id);

  const toCheck = store.orders.filter((o) => o.status === 'in_review').length;
  const waiting = store.orders.filter((o) => o.status === 'waiting_payment').length;
  const live = store.orders.filter((o) => o.status === 'live');
  const paidTotal = store.orders.filter((o) => o.status !== 'waiting_payment').reduce((s, o) => s + o.paid, 0);
  // Split owners' 30% by the apps each live order shows in (views proxy until apps report counts).
  const viewsByApp: Record<string, number> = {};
  store.orders.filter((o) => o.status !== 'waiting_payment').forEach((o) => {
    const apps = tierById(o.tier).apps.filter((a) => a !== 'Next app');
    apps.forEach((a) => (viewsByApp[a] = (viewsByApp[a] ?? 0) + o.paid / apps.length));
  });
  const split = revenueSplit(paidTotal, viewsByApp);

  const stats = [
    {
      id: 'check',
      label: 'Ads to review',
      value: String(toCheck),
      icon: 'ri-shield-check-line',
      tone: 'primary' as const,
      delta: toCheck > 0 ? 'Pending check' : undefined,
      trend: toCheck > 0 ? ('flat' as const) : undefined,
      note: 'Creative review queue',
    },
    {
      id: 'wait',
      label: 'Waiting payment',
      value: String(waiting),
      icon: 'ri-bank-line',
      tone: 'secondary' as const,
      note: 'Pending bank wire transfer',
    },
    {
      id: 'live',
      label: 'Live sponsors',
      value: String(live.length),
      icon: 'ri-broadcast-line',
      tone: 'accent' as const,
      delta: 'Active ads',
      trend: 'up' as const,
      note: 'Broadcasting in apps',
    },
    {
      id: 'rev',
      label: 'Package revenue',
      value: usd(paidTotal),
      icon: 'ri-money-dollar-circle-line',
      tone: 'background' as const,
      note: 'Gross package total (ex VAT)',
    },
  ];

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading font-extrabold text-2xl text-foreground-950 tracking-tight">Sponsors</h3>
          <p className="text-sm text-foreground-500 mt-0.5">Confirm payments, check licences and ads, and put sponsor packages live.</p>
        </div>
        <Link
          to="/sponsor"
          className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-background-50 border border-background-200/90 text-sm font-semibold text-foreground-700 shadow-xs hover:bg-background-100 hover:border-background-300 transition-all duration-200"
        >
          <i className="ri-external-link-line text-primary-500"></i> Open sponsor portal
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {stats.map((s) => (
          <KpiCard
            key={s.id}
            label={s.label}
            value={s.value}
            icon={s.icon}
            tone={s.tone}
            delta={s.delta}
            trend={s.trend}
            note={s.note}
          />
        ))}
      </div>

      <div className="group relative overflow-hidden rounded-2xl border border-background-200/90 bg-background-50/95 p-5 md:p-6 shadow-sm transition-all duration-300 hover:shadow-lg flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h4 className="font-heading font-bold text-base md:text-lg text-foreground-950">Where the money goes</h4>
          <span className="text-xs font-semibold text-foreground-500">70% Platform · 30% App Partners</span>
        </div>
        <div className="flex h-11 rounded-xl overflow-hidden text-xs font-bold text-white shadow-inner">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 flex items-center justify-center px-3" style={{ flex: split.loyalty || 1 }}>
            Loyalty 70% · {usd(split.loyalty)}
          </div>
          {Object.entries(split.owners).map(([app, v], i) => (
            <div
              key={app}
              className={`${i ? 'bg-indigo-700' : 'bg-blue-600'} flex items-center justify-center px-3 min-w-0 truncate`}
              style={{ flex: v || 1 }}
            >
              {app} {usd(v)}
            </div>
          ))}
        </div>
        <p className="text-xs text-foreground-500">
          App owners share 30% of package money, split by views each app showed. Points packs go directly to the reward fund.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedControl options={FILTERS} value={filter} onChange={setFilter} />
        <span className="text-sm font-medium text-foreground-500">{orders.length} of {store.orders.length} orders</span>
      </div>

      <div className="rounded-2xl border border-background-200/90 bg-background-50/95 shadow-sm overflow-hidden overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-foreground-400 border-b border-background-200/80 bg-background-100/50">
              <th className="px-5 py-3.5 font-bold">Sponsor</th>
              <th className="px-5 py-3.5 font-bold">Licence</th>
              <th className="px-5 py-3.5 font-bold">Package</th>
              <th className="px-5 py-3.5 font-bold">Ads</th>
              <th className="px-5 py-3.5 font-bold">Status</th>
              <th className="px-5 py-3.5 font-bold text-right">Total</th>
              <th className="px-5 py-3.5 font-bold text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-background-200/70">
            {orders.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-foreground-500 font-medium">No orders found matching this filter.</td></tr>
            )}
            {orders.map((o) => {
              const acc = account(o.accountId);
              const t = tierById(o.tier);
              return (
                <tr key={o.id} className="hover:bg-background-100/60 transition-colors align-top">
                  <td className="px-5 py-4">
                    <p className="font-bold text-foreground-950">{o.company}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">{o.no} · {o.contact} · {o.phone}</p>
                    <p className="text-xs text-foreground-400">{acc?.businessType} · ordered {fmtDate(o.createdAt)}</p>
                  </td>
                  <td className="px-5 py-4">
                    <p className="font-semibold text-foreground-900">{acc?.licence}</p>
                    <p className="text-xs text-foreground-500">{acc?.licencePhoto || 'no photo'}</p>
                    <p className={`text-xs font-bold mt-1 ${acc?.status === 'verified' ? 'text-emerald-700' : acc?.status === 'rejected' ? 'text-rose-700' : 'text-foreground-500'}`}>
                      {acc?.status === 'verified' ? '✓ Verified' : acc?.status === 'rejected' ? '✗ Rejected' : 'Not checked'}
                    </p>
                  </td>
                  <td className="px-5 py-4">
                    <p className="font-semibold text-foreground-950">{t.name}{o.segment === 'ngo' ? ' · NGO' : ''} · {o.months} mo</p>
                    <p className="text-xs text-foreground-500">{fmtDate(o.start)} – {fmtDate(o.end)}</p>
                    <p className="text-xs text-foreground-400">{o.provinces.join(', ')}</p>
                  </td>
                  <td className="px-5 py-4">
                    <ul className="flex flex-col gap-1 text-xs">
                      {o.ads.map((a) => <li key={a.id}><span className="text-foreground-500 font-medium">{a.type}:</span> {a.title}</li>)}
                    </ul>
                    {o.link && <a href={o.link} target="_blank" rel="noreferrer noopener" className="text-xs text-primary-600 underline break-all mt-1 block">{o.link}</a>}
                    {o.changeNote && <p className="text-xs text-rose-700 mt-1 font-medium">Asked: {o.changeNote}</p>}
                  </td>
                  <td className="px-5 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap shadow-2xs ${statusTone[o.status]}`}>{ORDER_STATUS_LABEL[o.status]}</span>
                    <p className="text-xs text-foreground-500 mt-1.5">{o.method === 'bank' ? 'Bank transfer' : o.method === 'khqr' ? 'KHQR' : 'Card'}</p>
                  </td>
                  <td className="px-5 py-4 text-right font-heading font-extrabold text-foreground-950">
                    {usd(o.paid)}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex flex-col items-end gap-1.5">
                      {o.status === 'in_review' && (
                        <>
                          <button type="button" onClick={() => approveOrder(o.id)} className="px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-700 border border-emerald-500/20 text-xs font-bold hover:bg-emerald-500/20 transition-colors cursor-pointer">
                            Approve
                          </button>
                          <button type="button" onClick={() => setChangeFor(o)} className="px-3 py-1.5 rounded-lg bg-secondary-100 text-secondary-800 text-xs font-bold hover:bg-secondary-200 transition-colors cursor-pointer">
                            Request changes
                          </button>
                          {acc && acc.status !== 'rejected' && (
                            <button type="button" onClick={() => rejectAccount(acc.id)} className="text-xs text-rose-600 hover:underline">
                              Reject company
                            </button>
                          )}
                        </>
                      )}
                      {o.status === 'waiting_payment' && (
                        <button type="button" onClick={() => confirmTransfer(o.id)} className="px-3 py-1.5 rounded-lg bg-primary-500/10 text-primary-700 border border-primary-500/20 text-xs font-bold hover:bg-primary-500/20 transition-colors cursor-pointer">
                          Confirm transfer
                        </button>
                      )}
                      {o.status === 'live' && (
                        <button type="button" onClick={() => endOrder(o.id)} className="text-xs text-foreground-500 hover:text-foreground-800 underline">
                          End campaign
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal open={!!changeFor} title="Request changes from sponsor" onClose={() => setChangeFor(null)}>
        {changeFor && (
          <div className="flex flex-col gap-4 text-sm">
            <p className="text-foreground-600">Select reason for changes:</p>
            <div className="flex flex-col gap-2">
              {CHANGE_REASONS.map((r) => (
                <label key={r} className="flex items-center gap-2 p-2.5 rounded-xl border border-background-200 hover:bg-background-100 cursor-pointer">
                  <input type="radio" name="reason" checked={note === r} onChange={() => setNote(r)} />
                  <span>{r}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setChangeFor(null)} className="px-4 py-2 rounded-xl border border-background-200 font-semibold hover:bg-background-100">Cancel</button>
              <button type="button" onClick={() => { requestChanges(changeFor.id, note); setChangeFor(null); }} className="px-4 py-2 rounded-xl bg-primary-500 text-white font-bold hover:bg-primary-600">Send request</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
